import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import TransactionTypeStatusSticker from 'dashboard/components/TransactionTypeStatusSticker/TransactionTypeStatusSticker';
import { MonoSpan } from 'dashboard/components/Typography';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import PlatformConnectionsModalContent from 'dashboard/components-lib/PlatformConnectionsModalContent';
import SearchInput from 'dashboard/components-lib/SearchInput';
import FormCreateTransactionType, {
  FormCreateTransactionTypeData,
} from 'dashboard/forms/FormCreateTransactionType/FormCreateTransactionType';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import {
  TransactionTypeFilterOption,
  TransactionTypeStatus,
} from 'dashboard/models/transaction-type';
import { TransactionTypesFilterParams } from 'dashboard/redux/actions/transaction-types';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS: {
  label: string;
  value: TransactionTypeStatus;
}[] = [
  {
    label: 'Active',
    value: TransactionTypeStatus.Active,
  },
  {
    label: 'Inactive',
    value: TransactionTypeStatus.Inactive,
  },
];

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const SearchContainer = styled('div')`
  grid-column: 1 / 4;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-column: auto;
  }
`;

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
`;

const Columns = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const ColumnCard = styled(Card)`
  width: 100%;
  flex-shrink: 1;
`;

enum ModalActionTypes {
  Activate = 'activate',
  Deactivate = 'deactivate',
}

interface ModalActionConfig {
  transactionType: TransactionTypeFilterOption;
  modalActionType: ModalActionTypes;
}

interface DashboardTransactionTypesViewProps {
  filters: TransactionTypesFilterParams;
  isLoading: boolean;
  transactionTypeBasics: TransactionTypeFilterOption[];
  createTransactionType: (args: FormCreateTransactionTypeData) => Promise<void>;
  handleDeactivateTransactionType: (transactionTypeId: string) => void;
  handleActivateTransactionType: (transactionTypeId: string) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleStatusFilterUpdate: (status: TransactionTypeStatus[]) => void;
}

const DashboardTransactionTypesView = ({
  filters,
  isLoading,
  transactionTypeBasics,
  createTransactionType,
  handleDeactivateTransactionType,
  handleActivateTransactionType,
  handleOmniSearch,
  handleStatusFilterUpdate,
}: DashboardTransactionTypesViewProps) => {
  const { omni, status } = filters;
  const hasEditPermission = usePermission(Permission.TransactionTypeWrite);
  const hasCreatePermission = usePermission(Permission.TransactionTypeCreate);

  const [templateForModalAction, setTemplateForModalAction] = useState<ModalActionConfig | null>(
    null,
  );
  const [shouldShowConfirmationModal, { prompt, confirm, cancel }] = useConfirmation();

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });
  const [
    createTransactionTypeModal,
    { enable: showCreateTransactionTypeModal, disable: hideCreateTransactionTypeModal },
  ] = useToggle(false);

  const location = useLocation();
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateTransactionTypeModalParam = queryParams.get('openCreateTransactionTypeModal');
    if (openCreateTransactionTypeModalParam === 'true' && hasCreatePermission) {
      showCreateTransactionTypeModal();
    }
  }, [location, hasCreatePermission]);

  const handleCreateTransactionType = async (args: FormCreateTransactionTypeData) => {
    createTransactionType(args);
    hideCreateTransactionTypeModal();
  };

  const handleModalActionClick = useCallback(
    (transactionType: TransactionTypeFilterOption, modalActionType: ModalActionTypes) => {
      setTemplateForModalAction({
        transactionType,
        modalActionType,
      });
      prompt(() => {
        switch (modalActionType) {
          case ModalActionTypes.Deactivate:
            handleDeactivateTransactionType(transactionType.id);
            break;
          case ModalActionTypes.Activate:
            handleActivateTransactionType(transactionType.id);
            break;
        }
        setTemplateForModalAction(null);
      });
    },
    [handleActivateTransactionType, handleDeactivateTransactionType],
  );

  const modalActionName: string = useMemo(() => {
    switch (templateForModalAction?.modalActionType) {
      case ModalActionTypes.Deactivate:
        return 'Deactivate';
      case ModalActionTypes.Activate:
        return 'Activate';
      default:
        return '';
    }
  }, [templateForModalAction?.modalActionType]);

  return (
    <>
      <Container>
        <ModalWrapper
          isOpen={createTransactionTypeModal}
          handleCloseModal={hideCreateTransactionTypeModal}
        >
          <ModalContainer>
            <ModalHeader onClose={hideCreateTransactionTypeModal}>
              Create Transaction Type
            </ModalHeader>
            <ModalBody>
              <FormCreateTransactionType
                handleClose={hideCreateTransactionTypeModal}
                onSubmit={handleCreateTransactionType}
              />
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>

        <ModalWrapper isOpen={shouldShowConfirmationModal} handleCloseModal={cancel}>
          <ConfirmationModal
            size='lg'
            header={`${modalActionName} this template?`}
            confirmActionText={modalActionName}
            confirmButtonType='primary'
            cancelAction={cancel}
            confirmAction={confirm}
          >
            <Paragraph css={{ whiteSpace: 'pre-wrap' }}>
              <strong>{templateForModalAction?.transactionType.name}</strong>
              {` will be ${modalActionName.toLowerCase()}d.`}
              <Spacer height={1} />

              <FeatureFlagged featureFlag='global:enable-platform-dependency-modal'>
                {templateForModalAction?.transactionType.id &&
                  templateForModalAction?.modalActionType === ModalActionTypes.Deactivate && (
                    <PlatformConnectionsModalContent
                      platformNodeId={templateForModalAction?.transactionType.id}
                    />
                  )}
              </FeatureFlagged>
            </Paragraph>
          </ConfirmationModal>
        </ModalWrapper>

        <InactiveEnvironmentBanner />
        <Header title='Transaction Types'>
          <ButtonGroup>
            <Permissioned
              permission={[Permission.TransactionTypeWrite, Permission.TransactionTypeCreate]}
            >
              <Button
                id='transaction-types__create-transaction-type-button'
                variant='primary'
                onClick={showCreateTransactionTypeModal}
                prefixIcon={<PlusIcon />}
              >
                Create type
              </Button>
            </Permissioned>
          </ButtonGroup>
        </Header>
        <Section>
          <Card>
            <Card.Body>
              <FilterRow>
                <SearchContainer>
                  <SearchInput
                    id='transaction-types-table__search'
                    handleInput={handleOmniSearch}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    value={omni}
                    placeholder='Search by transaction type ID or name'
                    size='md'
                  />
                </SearchContainer>
                <div>
                  <FormControlLabel>Status</FormControlLabel>
                  <MultiSelect
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    value={status}
                    selectOptionValues={STATUS_FILTER_OPTIONS}
                    labelTextMapper={statusFilterLabelTextMapper}
                    onChange={handleStatusFilterUpdate}
                    size='md'
                  />
                </div>
              </FilterRow>
            </Card.Body>
          </Card>
          <Columns>
            <ColumnCard>
              <Card.Body>
                <Card.Title>Transaction type list</Card.Title>
                <TableLayout
                  dataTest='transaction-types-table'
                  isLoading={isLoading}
                  showHeader={true}
                  isEmpty={transactionTypeBasics.length === 0}
                  nullState={
                    <TableNullState dataTestBlock='transaction-types-table__null-state'>
                      <Permissioned
                        permission={Permission.TransactionTypeView}
                        fallback={
                          <div>
                            User permission required to view transaction type details. Please
                            contact your account admin if you&#39;d like to view transaction types.
                          </div>
                        }
                      >
                        <h3>Looks like there aren&#39;t any transaction types yet.</h3>
                        <p>Create a transaction type to get started.</p>
                      </Permissioned>
                    </TableNullState>
                  }
                  headerTR={
                    <>
                      <TableTH>Name</TableTH>
                      <TableTH>ID</TableTH>
                      <TableTH>Status</TableTH>
                      <TableTH>
                        Last updated ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
                      </TableTH>
                      <TableTH isSticky>{/* Actions column */}</TableTH>
                    </>
                  }
                >
                  {transactionTypeBasics.map((transactionTypeBasic) => {
                    const status = transactionTypeBasic['status'];
                    const inactive = status === TransactionTypeStatus.Inactive;
                    return (
                      <TableLinkTR
                        to={`/dashboard/transaction-types/${transactionTypeBasic.id}`}
                        id={`transaction-types-table__row--${transactionTypeBasic.id}`}
                        key={transactionTypeBasic.id}
                      >
                        <TableTD>{transactionTypeBasic.name}</TableTD>
                        <TableTD>
                          <HoverCopyableField
                            copyValue={transactionTypeBasic.id}
                            confirmationText='Copied Transaction Type ID'
                          >
                            <MonoSpan>{transactionTypeBasic.id}</MonoSpan>
                          </HoverCopyableField>
                        </TableTD>
                        <TableTD>
                          <TransactionTypeStatusSticker status={status} />
                        </TableTD>
                        <TableTD>
                          {transactionTypeBasic['updated-at']
                            ? DateTime.fromISO(transactionTypeBasic['updated-at']).toFormat(
                                DATE_AND_TIME_FORMAT,
                              )
                            : undefined}
                        </TableTD>
                        {hasEditPermission && (
                          <TableActionTD>
                            <DropdownMenu
                              trigger={
                                <Button
                                  aria-label={`More actions for ${transactionTypeBasic.id}`}
                                  id={`transaction-types-table__-menu-${transactionTypeBasic.id}`}
                                  variant='basic'
                                  width='icon'
                                  onClick={(e) => {
                                    // Avoid clicking table row
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <MoreIcon />
                                </Button>
                              }
                            >
                              <DropdownMenu.Item
                                id={`transaction-types-table__row-menu-action-${transactionTypeBasic.id}`}
                                variant={inactive ? 'basic' : 'danger'}
                                onClick={(e) => {
                                  // Avoid clicking table row
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleModalActionClick(
                                    transactionTypeBasic,
                                    inactive
                                      ? ModalActionTypes.Activate
                                      : ModalActionTypes.Deactivate,
                                  );
                                }}
                              >
                                {inactive ? 'Activate' : 'Deactivate'}
                              </DropdownMenu.Item>
                            </DropdownMenu>
                          </TableActionTD>
                        )}
                      </TableLinkTR>
                    );
                  })}
                </TableLayout>
              </Card.Body>
            </ColumnCard>
          </Columns>
        </Section>
      </Container>
    </>
  );
};

export default DashboardTransactionTypesView;
